import { get } from 'lodash';

import { Delta } from 'quill';

import { QuillTemplateLocation } from '../../../generated/graphql.generated';

import { mapJobToTemplateVariables } from './job-variables.mapper';
import { VARIABLE_BLOT_NAME, VariableBlot } from './variable.blot';
import { QuillVariableType, QuillVariable } from './variableToolbar.quillModule';

// Type for the data produced by mapJobToTemplateVariables (formerly jobToolFeature.selectJobTemplateVariablesData)
export type JobTemplateVariablesData = ReturnType<typeof mapJobToTemplateVariables>;

export const COMMENT_MODES_TEMPLATES = {
    'Comment': QuillTemplateLocation.GenericJobComment,
    'Send Email to Customer': QuillTemplateLocation.JobCommentSendEmailToCustomer,
    'Send SMS to Customer': QuillTemplateLocation.JobCommentSendSmsToCustomer,
    'Notify Job Watchers': QuillTemplateLocation.JobCommentNotifyJobWatchers,
    'Ask Bee': QuillTemplateLocation.JobCommentAskBee,
    'Schedule Communication': QuillTemplateLocation.JobCommentScheduleCommunication,
}

export const JOB_TEMPLATE_VARIABLES = [
    { title: 'Customer Name', dataKey: 'misc.customer.name', dataType: QuillVariableType.TEXT },
    { title: 'Customer - given name', dataKey: 'misc.customer.givenName', dataType: QuillVariableType.TEXT },
    { title: 'Customer - family name', dataKey: 'misc.customer.familyName', dataType: QuillVariableType.TEXT },
    { title: 'Customer - company', dataKey: 'misc.customer.company', dataType: QuillVariableType.TEXT },
    { title: 'Customer - email', dataKey: 'misc.customer.email', dataType: QuillVariableType.TEXT },
    { title: 'Customer - phone', dataKey: 'misc.customer.phone', dataType: QuillVariableType.TEXT },
    { title: 'Customer Type', dataKey: 'job.metadata.jobType', dataType: QuillVariableType.TEXT },
    { title: 'Job Origin', dataKey: 'job.metadata.jobOrigin', dataType: QuillVariableType.TEXT },
    { title: 'Job Source', dataKey: 'misc.howDidTheyHearAboutUs', dataType: QuillVariableType.TEXT },

    { title: 'Move Date', dataKey: 'misc.moveDate', dataType: QuillVariableType.DATE, format: 'long' },

    { title: 'Travel time to first location', dataKey: 'misc.travelTimeToFirstLocation', dataType: QuillVariableType.TEXT },
    { title: 'Start Location', dataKey: 'misc.locations.start.addressString', dataType: QuillVariableType.TEXT },
    { title: 'Start Location - Details', dataKey: 'misc.locations.start.details', dataType: QuillVariableType.TEXT },
    { title: 'Start Location - Access', dataKey: 'misc.locations.start.accessInformation', dataType: QuillVariableType.TEXT },
    { title: 'Start Location - Parking', dataKey: 'misc.locations.start.parkingInformation', dataType: QuillVariableType.TEXT },

    { title: 'End Location', dataKey: 'misc.locations.end.addressString', dataType: QuillVariableType.TEXT },
    { title: 'End Location - Details', dataKey: 'misc.locations.end.details', dataType: QuillVariableType.TEXT },
    { title: 'End Location - Access', dataKey: 'misc.locations.end.accessInformation', dataType: QuillVariableType.TEXT },
    { title: 'End Location - Parking', dataKey: 'misc.locations.end.parkingInformation', dataType: QuillVariableType.TEXT },

    { title: 'Sales Agent', dataKey: 'misc.salesAgent.name', dataType: QuillVariableType.TEXT },
    { title: 'Estimator', dataKey: 'misc.estimator.name', dataType: QuillVariableType.TEXT },
];
export const DISPATCH_TEMPLATE_VARIABLES = [
    { title: 'Attendee Name', dataKey: 'user.name', dataType: QuillVariableType.TEXT },
    { title: 'Dispatch Date', dataKey: 'events[0].start', dataType: QuillVariableType.DATE, format: 'long' },
    { title: 'Arrival Time', dataKey: 'events[0].start', dataType: QuillVariableType.DATE, format: 'long' },
    { title: 'Arrival Location', dataKey: 'events[0].locations[0].location.addressLineOne', dataType: QuillVariableType.TEXT },
    { title: 'Estimated Working Time', dataKey: 'misc.estWorkingTime', dataType: QuillVariableType.TEXT },
    { title: 'Itinerary', dataKey: 'misc.itinerary', dataType: QuillVariableType.TEXT },
];

export const TEMPLATE_VARIABLES = {
    [QuillTemplateLocation.GenericJobComment]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobCommentSendEmailToCustomer]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobCommentSendSmsToCustomer]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobCommentNotifyJobWatchers]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobCommentAskBee]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobCommentScheduleCommunication]: JOB_TEMPLATE_VARIABLES,

    [QuillTemplateLocation.JobSummary]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobCrewSummary]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobAdminSummary]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.JobCustomerSummary]: JOB_TEMPLATE_VARIABLES,

    [QuillTemplateLocation.DispatchComment]: DISPATCH_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.DispatchSchedule]: DISPATCH_TEMPLATE_VARIABLES,

    [QuillTemplateLocation.EstimateSendEmail]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.EstimateSendSms]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.ContractSendEmail]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.ContractSendSms]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.InvoiceSendEmail]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.InvoiceSendSms]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.ReceiptSendEmail]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.ReceiptSendSms]: JOB_TEMPLATE_VARIABLES,
    [QuillTemplateLocation.BillOfLading]: JOB_TEMPLATE_VARIABLES,
}


export function replaceQuillVariablesWithActualValues(
    sourceContents: Delta,
    data: any,
): Delta {
    if (!sourceContents || !sourceContents.ops || !Array.isArray(sourceContents.ops)) {
        return new Delta();
    }

    const result = new Delta();

    for (const op of sourceContents.ops) {
        const variableDetails = op?.insert?.[VARIABLE_BLOT_NAME] as QuillVariable | undefined;
        if (variableDetails) {
            result.insert(
                VariableBlot.formatValue(
                    get(data, variableDetails.dataKey),
                    variableDetails.dataType || QuillVariableType.TEXT,
                    variableDetails.format || ''
                )
            );
        } else {
            result.push(op);
        }
    }

    return result;
}

/**
 * Converts a Quill Delta object to plain text by extracting and concatenating all string inserts.
 * Only operations with string inserts are included; embedded objects or custom blots are ignored.
 *
 * @param contents - The Quill Delta to convert.
 * @returns The plain text representation of the Delta.
 *
 * Adapted from the Quill editor source:
 * https://github.com/slab/quill/blob/main/packages/quill/src/core/editor.ts#L211
 */
export function convertQuillContentsToText(contents: Delta): string {
    return contents
      .filter((op) => typeof op.insert === 'string')
      .map((op) => op.insert)
      .join('');
}
